import { Component, inject, OnInit, viewChild } from '@angular/core';
import { UltimateGenerateRequestArticleTypeEnum } from '@models/Sessions/Idea/podcast-api';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Idea } from '../../../../models/Sessions/Idea/Idea';
import {
  InputFormComponent,
  InputType,
} from '../new-idea-modal/input-form/input-form.component';

@Component({
  selector: 'app-regenerate-idea-modal',
  standalone: true,
  imports: [InputFormComponent],
  templateUrl: './regenerate-idea-modal.component.html',
  styleUrl: './regenerate-idea-modal.component.scss',
})
export class RegenerateIdeaModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  inputForm = viewChild(InputFormComponent);

  inputParams: Idea['inputParams'];

  ngOnInit(): void {
    if (!this.inputParams) {
      return;
    }

    const inputType =
      'subject' in this.inputParams
        ? InputType.Topic
        : 'mainText' in this.inputParams
          ? InputType.Text
          : InputType.Sources;

    this.inputForm().activeTab = inputType;
    if ('inputUrlOrTextList' in this.inputParams) {
      this.inputParams.inputUrlOrTextList
        .slice(1, this.inputParams.inputUrlOrTextList.length)
        .forEach(() => this.inputForm().addSource());
    }

    /* 
      TODO: WDV-112 remove this map after article type migration
    */
    const ARTICLE_TYPE_MAP: Record<
      string,
      UltimateGenerateRequestArticleTypeEnum
    > = {
      report: 'news_report',
      collector: 'collector',
      advisor: 'advice',
    };
    /** */

    this.inputForm().form.patchValue({
      guests: 'guests' in this.inputParams ? this.inputParams.guests : null,
      sections:
        'sections' in this.inputParams ? this.inputParams.sections : null,
      inputType: inputType,
      provideContext: !!this.inputParams.context,
      context: this.inputParams.context || null,
      ...(inputType === InputType.Topic && 'subject' in this.inputParams
        ? {
            topic: {
              name: this.inputParams.subject,
            },
          }
        : {}),
      ...(inputType === InputType.Text && 'mainText' in this.inputParams
        ? {
            text: {
              mainText: this.inputParams.mainText,
            },
          }
        : {}),
      ...(inputType === InputType.Sources && 'userTextType' in this.inputParams
        ? {
            sources: {
              items: this.inputParams.inputUrlOrTextList,
              language: this.inputParams.promptLanguage,
              style: ARTICLE_TYPE_MAP[this.inputParams.userTextType],
              context: this.inputParams.context,
            },
          }
        : {}),
    });
  }

  regenerate() {
    this.activeModal.close(this.getInputParams());
  }

  private getInputParams(): Idea['inputParams'] {
    const {
      guests: numberOfGuests,
      sections: numberOfSections,
      context,
      inputType,
      topic,
      text,
      sources,
    } = this.inputForm().form.getRawValue();

    switch (inputType) {
      case InputType.Topic: {
        return {
          context: context ?? undefined,
          guests: numberOfGuests,
          sections: numberOfSections,
          subject: topic.name,
          concept: topic.branstormConcepts.length
            ? topic.branstormConcepts
                .filter(v => v.selected)
                .map(v => v.concept)
            : undefined,
          includeResearch: true,
        };
      }
      case InputType.Text: {
        return {
          context: context ?? undefined,
          guests: numberOfGuests,
          sections: numberOfSections,
          mainText: text.mainText,
        };
      }
      case InputType.Sources: {
        return {
          context: sources.context,
          promptLanguage: sources.language,
          userTextType: sources.style,
          inputUrlOrTextList: sources.items.filter(Boolean),
        };
      }
      default:
        throw new Error('Unknown input type');
    }
  }
}
